import React from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Grid } from '@material-ui/core';
import Validation from '../../../Validation/Validation';
import TextInputFieldWithValidation from '../../../ValidationFormElements/TextInputFieldWithValidation';
import DateInputFieldWithValidation from '../../../ValidationFormElements/DateInputFieldWithValidation';
import MainSelect from '../../../MUI/MainSelect';
import { isArmenianCountry } from '../../../../helpers/countryHelper';

import commonStyles from '../../formGroupsCommon.module.scss';

const PassportIdInfo = ({
  formProps,
  citizenship,
  countries,
  getLabelWithTranslation,
  handleCitizenshipChange,
  passportIdInfo,
  handleChangeInput,
  issueDate,
  validityDate,
  handleIssueDateChange,
  setValidityDate,
}) => {
  const { t } = useTranslation();
  const { errors, setValue, clearError, triggerValidation } = formProps;

  return (
    <Grid container spacing={4}>
      <Grid item xs={12} md={6}>
        <Validation name="citizenship" errors={errors} clearError={clearError}>
          <MainSelect
            label="passportIdInfo.citizenship"
            value={citizenship}
            options={countries}
            getMenuLabel={getLabelWithTranslation}
            onChange={(value) => {
              handleCitizenshipChange(value, true);
              triggerValidation({ name: 'socCard' });
            }}
            required
          />
        </Validation>
      </Grid>
      <Grid item xs={12} md={6}>
        <TextInputFieldWithValidation
          required={isArmenianCountry(citizenship)}
          formProps={formProps}
          labelKey="passportIdInfo.socialCardNumber"
          name="socCard"
          autoComplete="off"
          maxLength={10}
          value={passportIdInfo.socCard}
          handleChange={(e) => handleChangeInput('socCard', e.target.value)}
        />
        <div className={commonStyles.hint}>{t('passportIdInfo.sSNCardHint')}</div>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <TextInputFieldWithValidation
              required
              formProps={formProps}
              labelKey="passportIdInfo.passportNumber"
              name="passport"
              autoComplete="off"
              value={passportIdInfo.passport}
              handleChange={(e) => handleChangeInput('passport', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextInputFieldWithValidation
              required
              formProps={formProps}
              labelKey="passportIdInfo.authority"
              name="passportFrom"
              autoComplete="off"
              value={passportIdInfo.passportFrom}
              handleChange={(e) => handleChangeInput('passportFrom', e.target.value)}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <DateInputFieldWithValidation
              formProps={{ errors, clearError }}
              labelKey="passportIdInfo.issueDate"
              name="passportGiven"
              value={issueDate}
              handleChange={handleIssueDateChange}
              maxDate={new Date()}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DateInputFieldWithValidation
              formProps={{ errors, clearError }}
              labelKey="passportIdInfo.validityDate"
              name="passportValidUntil"
              value={validityDate}
              handleChange={(date) => {
                setValidityDate(date);
                setValue('passportValidUntil', date, true);
              }}
              minDate={issueDate}
              required
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

PassportIdInfo.propTypes = {
  formProps: PropTypes.shape({
    register: PropTypes.func,
    setValue: PropTypes.func,
    getValues: PropTypes.func,
    triggerValidation: PropTypes.func,
    errors: PropTypes.shape({
      passportValidUntil: PropTypes.shape({}),
    }),
    clearError: PropTypes.func,
  }),
  citizenship: PropTypes.shape({
    id: PropTypes.number,
  }),
  countries: PropTypes.arrayOf(PropTypes.shape({})),
  getLabelWithTranslation: PropTypes.func,
  handleCitizenshipChange: PropTypes.func,
  handleChangeInput: PropTypes.func,
  handleIssueDateChange: PropTypes.func,
  setValidityDate: PropTypes.func,
  passportIdInfo: PropTypes.shape({
    passportFrom: PropTypes.string,
    passport: PropTypes.string,
    socCard: PropTypes.string,
  }),
  issueDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  validityDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
};

PassportIdInfo.defaultProps = {
  formProps: {},
  countries: [],
  citizenship: {},
  passportIdInfo: {},
  issueDate: null,
  validityDate: null,
  getLabelWithTranslation: () => {},
  handleCitizenshipChange: () => {},
  handleChangeInput: () => {},
  handleIssueDateChange: () => {},
  setValidityDate: () => {},
};

export default PassportIdInfo;
